import { createAction } from '@reduxjs/toolkit';
import _getOr from 'lodash/fp/getOr';
import _pipe from 'lodash/fp/pipe';
import _values from 'lodash/fp/values';
import _flatMap from 'lodash/fp/flatMap';
import _uniqBy from 'lodash/fp/uniqBy';
import _uniqWith from 'lodash/uniqWith';
import _isEqual from 'lodash/isEqual';

import {
  CALC_ENGINE_ACTION_PREFIX,
  LINKED_ADJUSTMENTS_BY_DOCUMENT_KEY,
} from '../utilities/constants';

const adjustmentToggled = createAction(
  `${CALC_ENGINE_ACTION_PREFIX}/adjustmentToggled`,
  (documentCode, adjustmentType, adjustmentName, active) => {
    return {
      payload: {
        documentCode,
        adjustmentType,
        adjustmentName,
        active,
      },
    };
  }
);

const matchesSource = (toggledAdjustment, linkedAdjustment) =>
  toggledAdjustment.documentCode === linkedAdjustment.sourceDocumentCode &&
  toggledAdjustment.adjustmentName === linkedAdjustment.sourceDisplayName;

const matchesTarget = (toggledAdjustment, linkedAdjustment) =>
  toggledAdjustment.documentCode === linkedAdjustment.targetDocumentCode &&
  toggledAdjustment.adjustmentName === linkedAdjustment.targetDisplayName;

const splatBySourceAndTarget = (la) => [
  {
    documentCode: la.sourceDocumentCode,
    adjustmentType: la.sourceAdjustmentType,
    adjustmentName: la.sourceDisplayName,
  },
  {
    documentCode: la.targetDocumentCode,
    adjustmentType: la.targetAdjustmentType,
    adjustmentName: la.targetDisplayName,
  },
];

export const findRelatedLinksForRoot = (rootAdjustment, allAdjustmentLinks) => {
  const matchingDirectDescendents = allAdjustmentLinks.filter(
    (l) => matchesSource(rootAdjustment, l) || matchesTarget(rootAdjustment, l)
  );

  const relatedLinks = matchingDirectDescendents.reduce((acc, cur) => {
    acc.push(...splatBySourceAndTarget(cur));
    return acc;
  }, []);

  return { rootAdjustment, relatedLinks };
};

const alreadySolved = (solutions, relatedAdjustment) => {
  const found = solutions.find((x) => {
    return (
      x.rootAdjustment.documentCode === relatedAdjustment.documentCode &&
      x.rootAdjustment.adjustmentType === relatedAdjustment.adjustmentType &&
      x.rootAdjustment.adjustmentName === relatedAdjustment.adjustmentName
    );
  });

  return Boolean(found);
};

/**
 * Recursively finds all adjustments that are linked together.
 * Given some linked adjustments in system like
 * 1 => 2
 * 2 => 4
 * 4 => 1
 * 3 => 5
 *
 * The algorithm uses a data structure of {rootAdjustment, relatedLinks}
 * to keep track of the direct descendent paths it has solved and only solves for new paths.
 *
 * The above example might work out like this:
 * round 1: [{rootAdjustment: 1, relatedLinks: [2]}]
 * round 2: [{rootAdjustment: 1, relatedLinks: [2]}, {rootAdjustment: 2, relatedLinks: [4]}]
 * round 3: - stops b/c it's already solved for 4
 */
export const recursivelyFindRelatedAdjustments = (
  rootAdjustment,
  allAdjustmentLinks,
  solutions = []
) => {
  const solutionForPath = findRelatedLinksForRoot(
    rootAdjustment,
    allAdjustmentLinks
  );

  solutions.push(solutionForPath);

  return solutionForPath.relatedLinks.reduce(
    (accumulatingSolutions, nextPath) => {
      if (!alreadySolved(accumulatingSolutions, nextPath)) {
        return recursivelyFindRelatedAdjustments(
          nextPath,
          allAdjustmentLinks,
          accumulatingSolutions
        );
      }
      return accumulatingSolutions;
    },
    solutions
  );
};

export const getAllRelatedLinksForSelectedAdjustment = (
  rootAdjustment,
  allAdjustmentLinks
) => {
  const solutions = recursivelyFindRelatedAdjustments(
    rootAdjustment,
    allAdjustmentLinks
  );

  const mergeRelatedLinks = solutions.reduce(
    (acc, cur) => {
      acc.push(...cur.relatedLinks);
      return acc;
    },
    [rootAdjustment]
  );

  const uniqueRelatedLinks = _uniqWith(mergeRelatedLinks, _isEqual);
  return uniqueRelatedLinks;
};

export const adjustmentToggledThunk =
  (documentCode, adjustmentType, adjustmentName, active) =>
  async (dispatch, getState) => {
    const getUniqueLinkedAdjustments = _pipe(
      _getOr(undefined, LINKED_ADJUSTMENTS_BY_DOCUMENT_KEY),
      _values,
      _flatMap(_values),
      _uniqBy('id')
    );

    /**
     * Each 'linked adjustment' is in state twice under its respective source and target documents
     * The _uniqBy('id') halves the amount of data to process
     */

    const allUniqueLinks = getUniqueLinkedAdjustments(getState());

    const adjustmentLinksToToggle = getAllRelatedLinksForSelectedAdjustment(
      {
        documentCode,
        adjustmentName,
        adjustmentType,
      },
      allUniqueLinks
    );

    adjustmentLinksToToggle.forEach((relatedLink) => {
      dispatch(
        adjustmentToggled(
          relatedLink.documentCode,
          relatedLink.adjustmentType,
          relatedLink.adjustmentName,
          active
        )
      );
    });
  };

export default adjustmentToggled;
